import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as authApi from '../api/authApi.js';
import * as accountApi from '../../account/api/accountApi.js';
import {
  signInRequested, socialSignInRequested, signInSucceeded, signInFailed,
  ssoValidateRequested, ssoValidateFailed,
  otpRequested, otpRequestSucceeded, otpRequestFailed,
  otpVerifyRequested, otpVerifyFailed, signupContextCleared,
  forgotRequested, forgotSucceeded, forgotFailed,
  signedOut,
} from './authSlice.js';
import { selectSignupContext } from './selectors.js';
import { history } from '../../../lib/history.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { toastShown } from '../../system/store/toastSlice.js';
import { inferOnboardingPending, syncStoredOnboardingPending } from '../lib/onboardingStatus.js';
import { persistSsoContext, sanitizeReturnPath } from '../lib/ssoParams.js';
import { getPostAuthDestination } from '../lib/authRedirect.js';

function friendlyAuthErrorMessage(err) {
  const status = err?.response?.status;
  if (status === 401) return "We couldn't sign you in. Check your credentials and try again.";
  if (status >= 500) return "Our service is having trouble right now. Please try again in a moment.";
  if (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK') {
    return "We couldn't reach the network. Check your connection and try again.";
  }
  return "We couldn't sign you in. Please try again.";
}

function friendlyOtpErrorMessage(err) {
  const status = err?.response?.status;
  if (status === 401 || status === 400) return "That code didn't match. Try again or request a new one.";
  if (status >= 500) return "Our service is having trouble right now. Please try again in a moment.";
  if (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK') {
    return "We couldn't reach the network. Check your connection and try again.";
  }
  return 'Could not verify the code. Please try again.';
}

function resolveOtpUserId(data = {}) {
  return data?.user_id
    ?? data?.userId
    ?? data?.login_user_id
    ?? data?.loginUserId
    ?? data?.id
    ?? null;
}

function persistSession({ token, userId }) {
  if (token) localStorage.setItem('token', token);
  else localStorage.removeItem('token');
  if (userId !== undefined && userId !== null) localStorage.setItem('login_user_id', userId);
}

function buildAuthPayload(data = {}, onboardingPending) {
  const { user_id, user_orgid, token, onboarding, ...rest } = data;
  return {
    token: token ?? null,
    user: { user_id, onboarding, ...rest },
    org: user_orgid ? { id: String(user_orgid), name: '' } : null,
    onboardingPending,
  };
}

function buildSsoAuthPayload(data = {}, onboardingPending, context = {}) {
  return {
    ...buildAuthPayload(data, onboardingPending),
    loginSource: context.referer === 'landing' ? 'landing-sso' : 'sso',
    ssoContext: context,
  };
}

function resolveSocialLoginSource(domain) {
  if (domain === 'google') return 'google';
  if (domain === 'fb' || domain === 'facebook') return 'facebook';
  return 'social';
}

function mergeProfileIntoAuthData(authData = {}, profileData = {}) {
  if (!profileData || typeof profileData !== 'object') return authData;
  const orgId = authData.user_orgid ?? profileData.org_id ?? profileData.user_orgid;
  return {
    ...authData,
    ...profileData,
    user_id: authData.user_id ?? profileData.user_id,
    user_orgid: orgId,
    token: authData.token,
    onboarding: profileData.onboarding ?? authData.onboarding,
    new_user: authData.new_user ?? authData.newUser ?? profileData.new_user ?? profileData.newUser,
  };
}

function* fetchProfileForAuth(authData) {
  if (!authData?.token && !localStorage.getItem('token')) return authData;
  try {
    const profileRes = yield call(accountApi.fetchProfile);
    if (profileRes?.status && profileRes.code === 200) {
      return mergeProfileIntoAuthData(authData, profileRes.data);
    }
  } catch (err) {
    captureSagaError(err, { action: 'auth/fetchProfileForAuth', background: true });
  }
  return authData;
}

export function* handleSignIn(action) {
  try {
    const res = yield call(authApi.login, action.payload);
    if (res && res.status !== false && res.code === 200) {
      const authData = res.data || {};
      persistSession({ token: authData.token, userId: authData.user_id });
      const resolvedData = yield call(fetchProfileForAuth, authData);
      const onboardingPending = inferOnboardingPending(resolvedData);
      yield put(signInSucceeded({ ...buildAuthPayload(resolvedData, onboardingPending), loginSource: 'password' }));
      syncStoredOnboardingPending(onboardingPending);
      history.push(getPostAuthDestination(action.payload?.from, onboardingPending));
    } else {
      yield put(signInFailed(res?.message || 'Sign in failed'));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyAuthErrorMessage(err);
    yield put(signInFailed(friendly));
    yield put(toastShown({ variant: 'error', message: friendly }));
  }
}

export function* handleSocialSignIn(action) {
  try {
    const res = yield call(authApi.socialLogin, action.payload);
    if (res && res.status !== false && res.code === 200) {
      const authData = res.data || {};
      persistSession({ token: authData.token, userId: authData.user_id });
      const resolvedData = yield call(fetchProfileForAuth, authData);
      const onboardingPending = inferOnboardingPending(resolvedData);
      yield put(signInSucceeded({
        ...buildAuthPayload(resolvedData, onboardingPending),
        loginSource: resolveSocialLoginSource(action.payload?.socialDomain),
      }));
      syncStoredOnboardingPending(onboardingPending);
      history.push(getPostAuthDestination(action.payload?.from, onboardingPending));
    } else {
      yield put(signInFailed(res?.message || 'Social sign in failed'));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyAuthErrorMessage(err);
    yield put(signInFailed(friendly));
    yield put(toastShown({ variant: 'error', message: friendly }));
  }
}

export function* handleSsoValidate(action) {
  const token = action.payload?.token;
  const context = action.payload?.context ?? {};

  if (!token) {
    yield put(ssoValidateFailed('SSO token is missing. Please start again from your organization portal.'));
    return;
  }

  try {
    const res = yield call(authApi.validateSsoToken, token);
    if (res && res.status && res.code === 200) {
      const authData = { ...(res.data || {}), token: res.data?.token ?? token };
      persistSsoContext(context);
      persistSession({ token: authData.token, userId: authData.user_id });
      const resolvedData = yield call(fetchProfileForAuth, authData);
      const onboardingPending = inferOnboardingPending(resolvedData);
      yield put(signInSucceeded(buildSsoAuthPayload(resolvedData, onboardingPending, context)));
      syncStoredOnboardingPending(onboardingPending);
      history.push(getPostAuthDestination(context.returnPath, onboardingPending));
    } else {
      yield put(ssoValidateFailed(res?.message || 'Could not complete SSO sign in.'));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyAuthErrorMessage(err);
    yield put(ssoValidateFailed(friendly));
    yield put(toastShown({ variant: 'error', message: friendly }));
  }
}

export function* handleOtpRequest(action) {
  const { loginId, intent } = action.payload;
  const usesForgotPasswordEndpoint = intent === 'signup' || intent === 'forgot';
  try {
    if (usesForgotPasswordEndpoint) {
      localStorage.removeItem('login_user_id');
    }
    const res = yield call(usesForgotPasswordEndpoint ? authApi.requestPasswordReset : authApi.requestOtp, loginId);
    if (res && res.status !== false && res.code === 200) {
      const otpUserId = resolveOtpUserId(res.data);
      if (otpUserId !== null && otpUserId !== undefined) {
        localStorage.setItem('login_user_id', otpUserId);
        yield put(otpRequestSucceeded({ userId: otpUserId, data: res.data }));
      } else {
        yield put(otpRequestSucceeded());
      }
      history.push('/verify');
    } else {
      yield put(otpRequestFailed(res?.message || 'Could not send code'));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyOtpErrorMessage(err);
    yield put(otpRequestFailed(friendly));
    yield put(toastShown({ variant: 'error', message: friendly }));
  }
}

export function* handleOtpVerify(action) {
  const { otp } = action.payload;
  const ctx = yield select(selectSignupContext);
  if (!ctx) {
    yield put(otpVerifyFailed('No pending verification. Start over from sign-up.'));
    return;
  }
  try {
    const res = yield call(authApi.login, { user_name: ctx.loginId, password: otp });
    if (res && res.status && res.code === 200) {
      const authData = res.data || {};
      persistSession({ token: authData.token, userId: authData.user_id });
      const resolvedData = yield call(fetchProfileForAuth, authData);
      const onboardingPending = inferOnboardingPending(resolvedData, ctx.intent);
      yield put(signInSucceeded({ ...buildAuthPayload(resolvedData, onboardingPending), loginSource: 'otp' }));
      syncStoredOnboardingPending(onboardingPending);
      // Apply license key if signup collected one — fire-and-forget; surface failure as toast but
      // don't block navigation since the user is already authenticated. Note: the /licenses
      // endpoint returns { status: 'success' | 'failure', message }, NOT { status: true/false }.
      // See apps/legacy/src/redux/settings/settings.sagas.js:159.
      if (ctx.licenseKey) {
        try {
          const lic = yield call(authApi.applyLicenseKey, ctx.licenseKey);
          if (lic?.status !== 'success') {
            yield put(toastShown({ variant: 'warn', message: lic?.message || 'License key was not accepted.' }));
          }
        } catch (licErr) {
          captureSagaError(licErr, { action: 'applyLicenseKey' });
          yield put(toastShown({ variant: 'warn', message: 'License key could not be applied. You can try again from your account.' }));
        }
      }
      // Clear the signup bridge so back-navigation to /verify doesn't render
      // the form again. Persist transform also nulls this on rehydrate.
      yield put(signupContextCleared());
      const returnPath = ctx.intent === 'otp-login' ? sanitizeReturnPath(ctx.returnPath) : '/courses';
      history.push(getPostAuthDestination(returnPath, onboardingPending));
    } else {
      yield put(otpVerifyFailed(res?.message || "That code didn't match. Try again or request a new one."));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyOtpErrorMessage(err);
    yield put(otpVerifyFailed(friendly));
  }
}

export function* handleForgotPassword(action) {
  const { loginId } = action.payload;
  try {
    const res = yield call(authApi.requestPasswordReset, loginId);
    if (res && res.code === 200) {
      yield put(forgotSucceeded());
      history.push('/forgot-password');
    } else {
      yield put(forgotFailed(res?.message || 'Could not send code'));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyAuthErrorMessage(err);
    yield put(forgotFailed(friendly));
    yield put(toastShown({ variant: 'error', message: friendly }));
  }
}

export function* handleSignOut(action) {
  if (!action?.payload?.accountDeleted) {
    try {
      yield call(authApi.logout);
    } catch (err) {
      captureSagaError(err, { action: signedOut.type });
    }
  }
  localStorage.removeItem('token');
  localStorage.removeItem('login_user_id');
  localStorage.removeItem('TriggerBuyNow');
  localStorage.removeItem('paymentInProcess');
  localStorage.removeItem('referer');
  localStorage.removeItem('payment_type');
  localStorage.removeItem('payment_method');
  localStorage.removeItem('is_subscription');
  localStorage.removeItem('new_user');
  syncStoredOnboardingPending(false);
  history.push('/signin');
}

export default function* authSaga() {
  yield takeLatest(signInRequested.type, handleSignIn);
  yield takeLatest(socialSignInRequested.type, handleSocialSignIn);
  yield takeLatest(ssoValidateRequested.type, handleSsoValidate);
  yield takeLatest(otpRequested.type, handleOtpRequest);
  yield takeLatest(otpVerifyRequested.type, handleOtpVerify);
  yield takeLatest(forgotRequested.type, handleForgotPassword);
  yield takeLatest(signedOut.type, handleSignOut);
}
